import { decode } from 'blurhash'
import { avisarDeDrive } from '@/features/auth/VerificacionCuenta'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/cn'

/**
 * `<MediaSlot />` — el único componente que sabe de Google Drive (§7.1 del stack).
 *
 * Tres capas, de abajo hacia arriba:
 *  1. el degradado guardado (`blurhash`), que reserva el espacio y aparece al
 *     instante, así la grilla no salta (§8.2);
 *  2. la miniatura de Drive, liviana, que hace de portada;
 *  3. en modo `playing` y sólo para videos, el visor de Drive embebido, que
 *     es lo único que no dibuja la aplicación (§8.4). Una imagen no lo
 *     necesita: la miniatura ya es la imagen, y el visor le suma su propia
 *     barra y su encuadre, y queda gris cuando Drive pide iniciar sesión aunque
 *     el archivo sea público (pasa con una cuenta de Workspace abierta).
 *
 * La miniatura también es el detector de acceso: Drive la sirve con la sesión
 * de quien mira, así que si no llega es que esa cuenta no puede ver el
 * archivo —permiso faltante, o dos cuentas de Google abiertas (§12.4)—. Ahí
 * se avisa, para que el panel ofrezca la salida del §9 sin dejar de funcionar.
 */
export interface MediaSlotProps {
  driveFileId?: string | undefined
  mime?: string | undefined
  /** Cadena de ~30 caracteres. Sin ella se usa un degradado neutro. */
  blurhash?: string | undefined
  /**
   * `archivo` toma la proporción real del archivo y lo muestra entero, sin
   * recortar: las diseñadoras entregan el tamaño correcto y el portal no le
   * impone uno (decisión del 21/09/2026). Hasta que llega la miniatura se
   * reserva 4:5.
   */
  aspect?: '4:5' | '1:1' | '9:16' | 'archivo'
  /** Sólo con `archivo`: fija la proporción (ancho / alto), p. ej. la de la primera lámina. */
  proporcion?: number | undefined
  /** Sólo con `archivo`: se avisa la proporción medida del archivo. */
  alMedir?: ((proporcion: number) => void) | undefined
  /**
   * Sólo con `archivo`: alto máximo (CSS). Un archivo muy alto no se corta,
   * se angosta para entrar.
   */
  altoMaximo?: string
  /** `poster` muestra la miniatura; `playing` embebe el visor de Drive si es un video. */
  mode?: 'poster' | 'playing'
  /** Texto alternativo del contenido. */
  titulo?: string | undefined
  className?: string | undefined
  /**
   * Miniatura guardada de Drive (Fase 6). Se sirve sin la sesión de Google
   * del navegador, así que no depende de con qué cuenta esté abierto Drive.
   */
  miniaturaGuardada?: string | null | undefined
  /** Se avisa cuando el contenido no se puede mostrar a quien está mirando. */
  alFallar?: () => void
  /**
   * Ancho de la miniatura que se le pide a Drive. El calendario muestra
   * muchas a la vez y le alcanza con una chica (§8.3).
   */
  ancho?: number
}

const ASPECTO: Record<
  Exclude<MediaSlotProps['aspect'], 'archivo' | undefined>,
  string
> = {
  '4:5': 'aspect-feed',
  '1:1': 'aspect-cuadrado',
  '9:16': 'aspect-reel',
}

/** Cuánto se espera una miniatura a la vista antes de darla por perdida. */
const ESPERA_MINIATURA_MS = 12_000

const visorDeDrive = (id: string) => `https://drive.google.com/file/d/${id}/preview`
const miniaturaDeDrive = (id: string, ancho: number) =>
  `https://drive.google.com/thumbnail?id=${id}&sz=w${String(ancho)}`

export function MediaSlot({
  driveFileId,
  blurhash,
  aspect = '4:5',
  mode = 'poster',
  mime,
  titulo,
  className,
  alFallar,
  miniaturaGuardada = null,
  ancho = 800,
  proporcion,
  alMedir,
  altoMaximo,
}: MediaSlotProps) {
  const [miniatura, setMiniatura] = useState<'cargando' | 'ok' | 'falla'>('cargando')
  const [medida, setMedida] = useState<number | null>(null)
  // Si la guardada no carga (Google las rota), se prueba con la de siempre.
  const [reserva, setReserva] = useState(false)
  const delArchivo = aspect === 'archivo'
  const relacion = proporcion ?? medida ?? 4 / 5
  const embebido =
    Boolean(driveFileId) &&
    mode === 'playing' &&
    miniatura === 'ok' &&
    Boolean(mime?.startsWith('video/'))

  const fallar = () => {
    if (!driveFileId) return
    if (miniaturaGuardada && !reserva) {
      setReserva(true)
      return
    }
    setMiniatura('falla')
    avisarDeDrive(driveFileId, false)
    alFallar?.()
  }
  const alFallarRef = useRef(fallar)
  useEffect(() => {
    alFallarRef.current = fallar
  })

  // Una miniatura que no carga ni falla dejaba la pieza en blanco para siempre
  // y sin el aviso de la P04 (revisión OO del 23/09/2026, con la cuenta de
  // trabajo abierta). Si ya está a la vista y no llega en un rato, cuenta como
  // falla. Sólo a la vista: las de más abajo esperan a que se llegue a ellas.
  const marco = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = marco.current
    if (!el || !driveFileId || miniatura !== 'cargando') return
    let reloj: ReturnType<typeof setTimeout> | null = null
    const observador = new IntersectionObserver((entradas) => {
      if (entradas.some((e) => e.isIntersecting) && !reloj) {
        reloj = setTimeout(() => {
          alFallarRef.current()
        }, ESPERA_MINIATURA_MS)
      }
    })
    observador.observe(el)
    return () => {
      observador.disconnect()
      if (reloj) clearTimeout(reloj)
    }
  }, [driveFileId, miniatura, reserva])

  return (
    <div
      ref={marco}
      data-slot="media"
      data-embebido={embebido ? 'si' : 'no'}
      data-contenido={driveFileId ? miniatura : 'sin-archivo'}
      data-proporcion={delArchivo ? relacion.toFixed(3) : undefined}
      className={cn(
        'relative w-full overflow-hidden bg-borde',
        delArchivo ? 'mx-auto' : ASPECTO[aspect],
        className,
      )}
      style={
        delArchivo
          ? {
              aspectRatio: String(relacion),
              ...(altoMaximo && {
                width: `min(100%, calc(${altoMaximo} * ${relacion.toFixed(4)}))`,
              }),
            }
          : undefined
      }
    >
      {blurhash ? (
        <Degradado blurhash={blurhash} />
      ) : (
        <div
          className="absolute inset-0 bg-gradient-to-br from-borde to-lienzo"
          aria-hidden
        />
      )}

      {driveFileId && (
        <img
          key={`${driveFileId}${reserva ? '-reserva' : ''}`}
          src={
            miniaturaGuardada && !reserva
              ? `${miniaturaGuardada}=w${String(ancho)}`
              : miniaturaDeDrive(driveFileId, ancho)
          }
          alt={titulo ?? ''}
          loading="lazy"
          className={cn(
            'absolute inset-0 size-full transition-opacity',
            delArchivo ? 'object-contain' : 'object-cover',
            miniatura === 'ok' ? 'opacity-100' : 'opacity-0',
          )}
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget
            if (delArchivo && w > 0 && h > 0) {
              setMedida(w / h)
              alMedir?.(w / h)
            }
            setMiniatura('ok')
            avisarDeDrive(driveFileId, true)
          }}
          onError={fallar}
        />
      )}

      {embebido && driveFileId && (
        <VisorDrive
          key={driveFileId}
          driveFileId={driveFileId}
          titulo={titulo ?? 'Contenido de la publicación'}
          relacion={delArchivo ? relacion : RELACION[aspect]}
        />
      )}
    </div>
  )
}

const RELACION = { '4:5': 4 / 5, '1:1': 1, '9:16': 9 / 16 } as const

/** Alto al que se dibuja el visor de Drive antes de achicarlo. */
const ALTO_VISOR = 800
/**
 * Drive deja un margen propio a los costados del video: se le da ese lugar
 * al visor para que no lo tenga que sacar del video.
 */
const SOBRANTE = 36

/**
 * El visor de Drive embebido, para los videos.
 *
 * Por debajo de unos 400 px de ancho Drive entra en un modo compacto que
 * corre el video a la derecha, deja una franja negra y agrega bandas arriba:
 * es lo que pasaba con todos los reels en el panel. Así que el visor se dibuja
 * grande (800 px de alto, en la proporción del archivo), donde Drive muestra
 * el video entero con sus controles, y se achica con una escala hasta el
 * tamaño del marco. Los clics siguen llegando: el navegador los traslada a
 * través de la escala.
 *
 * El visor entra ENTERO en el marco y centrado (revisión OO del 23/09/2026):
 * antes se lo ensanchaba y se cortaban los costados, y como Drive no centra
 * el video, en el celular quedaba cortado a la derecha. Ahora el margen
 * negro de Drive se ve como bandas finas; el video, nunca cortado.
 *
 * El visor no se redimensiona: si su medida cambia, se crea uno nuevo (OBS-006).
 * El reproductor que Drive sirve en el iPhone se arma una sola vez con la
 * medida con que nace; achicarlo después dejaba el video corrido a la
 * derecha y cortado.
 */
function VisorDrive({
  driveFileId,
  titulo,
  relacion,
}: {
  driveFileId: string
  titulo: string
  relacion: number
}) {
  const caja = useRef<HTMLDivElement>(null)
  const [medida, setMedida] = useState({ ancho: 0, alto: 0 })

  useEffect(() => {
    const el = caja.current
    if (!el) return
    const medir = () => {
      setMedida({ ancho: el.clientWidth, alto: el.clientHeight })
    }
    medir()
    const observador = new ResizeObserver(medir)
    observador.observe(el)
    return () => {
      observador.disconnect()
    }
  }, [])

  const anchoVisor = Math.round(ALTO_VISOR * relacion) + SOBRANTE
  const escala =
    medida.alto > 0 ? Math.min(medida.alto / ALTO_VISOR, medida.ancho / anchoVisor) : 0
  return (
    <div
      ref={caja}
      data-visor-drive
      className="absolute inset-0 overflow-hidden bg-black"
    >
      {escala > 0 && (
        <iframe
          key={anchoVisor}
          src={visorDeDrive(driveFileId)}
          title={titulo}
          allow="autoplay"
          className="absolute top-1/2 left-1/2 border-0"
          style={{
            width: `${String(anchoVisor)}px`,
            height: `${String(ALTO_VISOR)}px`,
            transform: `translate(-50%, -50%) scale(${String(escala)})`,
            transformOrigin: 'center',
          }}
        />
      )}
    </div>
  )
}

/** Decodifica el blurhash una sola vez a un canvas de 32×40: es suficiente para un fondo. */
function Degradado({ blurhash }: { blurhash: string }) {
  const lienzo = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = lienzo.current
    if (!canvas) return

    const ancho = 32
    const alto = 40
    try {
      const pixeles = decode(blurhash, ancho, alto)
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const imagen = ctx.createImageData(ancho, alto)
      imagen.data.set(pixeles)
      ctx.putImageData(imagen, 0, 0)
    } catch {
      // Un degradado inválido no rompe la pantalla: queda el fondo neutro.
    }
  }, [blurhash])

  return (
    <canvas
      ref={lienzo}
      width={32}
      height={40}
      className="absolute inset-0 size-full"
      aria-hidden
    />
  )
}
